import { LeadStatusIcon } from '../icons/LeadStatusIcon';
import type { StatusBadgeConfigMap } from '@/components/ui/status-badge';
import { StatusActiveIcon, StatusCancelledIcon } from '@icons/StatusIcons';

// ─────────────────────────────────────────────────────────────────────────────
// Event info config — drives the status pill in the event card header (preview
// drawer GRAIN-1725 + expanded view GRAIN-2027, one shared component). Backend
// `event_status` is the AASM state; the UI collapses it to the Draft /
// Published / Cancelled the user reasons about. The status pill reuses the
// shared StatusBadge primitive (same as order statuses), styled per Figma node
// 7043:166739: draft = custard/butter "Pending" pill.
// Requirement/date chips are derived separately — see common/eventChips.ts.
// ─────────────────────────────────────────────────────────────────────────────

export type EventStatusKey = 'draft' | 'published' | 'cancelled';

// Mirrors FulfilmentEvent::PUBLISHED_EVENT_STATES.
const PUBLISHED_EVENT_STATES = ['confirmed', 'preparing', 'ready', 'delivered'];

export function resolveEventStatusKey(status: string | null | undefined): EventStatusKey {
  if (status === 'cancelled') return 'cancelled';
  if (status && PUBLISHED_EVENT_STATES.includes(status)) return 'published';
  return 'draft';
}

// Labels are injected (i18n) so the config can be built at render time.
export function createEventStatusBadgeConfig(
  labels: Record<EventStatusKey, string>,
): StatusBadgeConfigMap<EventStatusKey> {
  return {
    draft: {
      label: labels.draft,
      icon: <LeadStatusIcon className="flex-shrink-0 text-turmeric-yellow" aria-hidden="true" />,
      bgClassName: 'bg-custard-yellow',
      borderClassName: 'border-butter-yellow',
      textClassName: 'text-turmeric-yellow',
    },
    published: {
      label: labels.published,
      icon: <StatusActiveIcon className="size-4 flex-shrink-0 text-confirmed-green" />,
      bgClassName: 'bg-[#e3fcef]',
      borderClassName: 'border-[#8cd9c3]',
      textClassName: 'text-confirmed-green',
    },
    cancelled: {
      label: labels.cancelled,
      icon: <StatusCancelledIcon className="size-4 flex-shrink-0 text-chilli-red" />,
      bgClassName: 'bg-[#ffeef0]',
      borderClassName: 'border-[#f5a3ad]',
      textClassName: 'text-chilli-red',
    },
  };
}
